import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Icon, toast } from '@kognitos/lattice';
import { TooltipDisabledButton } from '@/shared/components';
import {
  getGetAutomationScheduleQueryKey,
  useCreateAutomationSchedule,
  useDeleteAutomationSchedule,
  useDisableAutomationSchedule,
  useEnableAutomationSchedule,
  useUpdateAutomationSchedule,
} from '@/generated/hooks/grimoire/v1/api';
import { captureException } from '@/modules/sentry';
import {
  errorHandler,
  checkIsAccessDeniedError,
} from '@/shared/utils/error-handler';
import {
  EScheduleActionType,
  ScheduleActionDialog,
} from './ScheduleActionDialog';
import type { IScheduleConfig } from './ScheduleTriggersDialog';
import { buildScheduleConfig } from './utils';
import { usePermissions } from '@/shared/permissions';

/**
 * Props for the ScheduleActionButtons component.
 */
export interface IScheduleActionButtonsProps {
  organizationId: string;
  workspaceId: string;
  automationId: string;
  /** Current schedule configuration based on inputs */
  config: IScheduleConfig;
  /** Whether the config is valid for saving/updating */
  isConfigValid: boolean;
  /** Whether the dialog is in view mode (schedule exists and not editing) */
  isViewMode: boolean;
  /** Whether the user is editing an existing schedule */
  isEditing: boolean;
  /** Callback to set editing state */
  setIsEditing: (editing: boolean) => void;
  /** Whether a save operation is in progress */
  isSaving: boolean;
  /** Callback to set saving state */
  setIsSaving: (saving: boolean) => void;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Whether the schedule is enabled (not paused) - only relevant when schedule exists */
  isScheduleEnabled?: boolean;
}

/**
 * Action buttons for the schedule triggers dialog footer.
 * Handles create, update, delete, edit, and cancel actions.
 *
 * @param props - Component props
 * @returns JSX element containing the action buttons
 */
export function ScheduleActionButtons({
  organizationId,
  workspaceId,
  automationId,
  config,
  isConfigValid,
  isViewMode,
  isEditing,
  setIsEditing,
  isSaving,
  setIsSaving,
  onClose,
  isScheduleEnabled,
}: IScheduleActionButtonsProps) {
  const { t } = useTranslation(['automation', 'common']);
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  // Check if can update schedule. Only one check needed here.
  const canUpdateSchedule = hasPermission(['UpdateAutomationSchedule'], {
    organization: organizationId,
    workspace: workspaceId,
  });

  const [actionDialogType, setActionDialogType] =
    useState<EScheduleActionType | null>(null);

  // API mutations
  const createScheduleMutation = useCreateAutomationSchedule();
  const deleteScheduleMutation = useDeleteAutomationSchedule();
  const updateScheduleMutation = useUpdateAutomationSchedule();
  const disableScheduleMutation = useDisableAutomationSchedule();
  const enableScheduleMutation = useEnableAutomationSchedule();

  const invalidateScheduleQuery = () => {
    queryClient.invalidateQueries({
      queryKey: getGetAutomationScheduleQueryKey(
        organizationId,
        workspaceId,
        automationId,
      ),
    });
  };

  // Create a new schedule on "Save" button click
  const handleSave = () => {
    setIsSaving(true);

    createScheduleMutation.mutate(
      {
        organization: organizationId,
        workspace: workspaceId,
        automation: automationId,
        data: {
          config: buildScheduleConfig(config),
        },
      },
      {
        onSuccess: () => {
          setIsSaving(false);
          invalidateScheduleQuery();
          // Without this the dialog stays open and the invalidated query flips
          // it into view mode, so a fresh save looks like it reopened as edit.
          onClose();
          toast.success(t('automation.schedule.toast.createSuccess'));
        },
        onError: error => {
          setIsSaving(false);

          errorHandler(error, () => {
            if (!checkIsAccessDeniedError(error)) {
              toast.error(t('automation.schedule.toast.createError'));
            }
            captureException(error as Error, {
              tags: {
                component: 'ScheduleActionButtons',
                action: 'handleSave',
              },
            });
          });
        },
      },
    );
  };

  // Go back to view only mode on "Cancel" button click
  const handleCancel = () => {
    if (isEditing) {
      setIsEditing(false);
    } else {
      onClose();
    }
  };

  // Open the delete dialog on "Delete" button click
  const handleDelete = () => {
    setActionDialogType(EScheduleActionType.DELETE);
  };

  // Switch to edit view mode on "Edit" button click
  const handleEdit = () => {
    setIsEditing(true);
  };

  // Open the update dialog on "Update" button click
  const handleUpdate = () => {
    setActionDialogType(EScheduleActionType.UPDATE);
  };

  // Open the pause dialog on "Pause" button click
  const handlePause = () => {
    setActionDialogType(EScheduleActionType.PAUSE);
  };

  // Open the resume dialog on "Resume" button click
  const handleResume = () => {
    setActionDialogType(EScheduleActionType.RESUME);
  };

  // Close the Delete or Update action dialog on "Cancel" button click
  const handleActionDialogOpenChange = (open: boolean) => {
    if (!open) {
      setActionDialogType(null);
    }
  };

  // Handle action confirmation (Delete, Update, Pause, Resume)
  const handleActionConfirm = () => {
    // Delete schedule
    if (actionDialogType === EScheduleActionType.DELETE) {
      deleteScheduleMutation.mutate(
        {
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
        },
        {
          onSuccess: () => {
            invalidateScheduleQuery();
            onClose();
            toast.success(t('automation.schedule.toast.deleteSuccess'));
          },
          onError: error => {
            setActionDialogType(null);

            errorHandler(error, () => {
              if (!checkIsAccessDeniedError(error)) {
                toast.error(t('automation.schedule.toast.deleteError'));
              }
              captureException(error as Error, {
                tags: {
                  component: 'ScheduleActionButtons',
                  action: 'handleDelete',
                },
              });
            });
          },
        },
      );
    }
    // Update schedule
    else if (actionDialogType === EScheduleActionType.UPDATE) {
      updateScheduleMutation.mutate(
        {
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
          data: {
            config: buildScheduleConfig(config),
          },
        },
        {
          onSuccess: () => {
            setIsEditing(false);
            setActionDialogType(null);
            invalidateScheduleQuery();
            toast.success(t('automation.schedule.toast.updateSuccess'));
          },
          onError: error => {
            setActionDialogType(null);

            errorHandler(error, () => {
              if (!checkIsAccessDeniedError(error)) {
                toast.error(t('automation.schedule.toast.updateError'));
              }
              captureException(error as Error, {
                tags: {
                  component: 'ScheduleActionButtons',
                  action: 'handleUpdate',
                },
              });
            });
          },
        },
      );
    }
    // Pause schedule
    else if (actionDialogType === EScheduleActionType.PAUSE) {
      disableScheduleMutation.mutate(
        {
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
          data: {},
        },
        {
          onSuccess: () => {
            invalidateScheduleQuery();
            onClose();
            toast.success(t('automation.schedule.toast.pauseSuccess'));
          },
          onError: error => {
            setActionDialogType(null);

            errorHandler(error, () => {
              if (!checkIsAccessDeniedError(error)) {
                toast.error(t('automation.schedule.toast.pauseError'));
              }
              captureException(error as Error, {
                tags: {
                  component: 'ScheduleActionButtons',
                  action: 'handlePause',
                },
              });
            });
          },
        },
      );
    }
    // Resume schedule
    else if (actionDialogType === EScheduleActionType.RESUME) {
      enableScheduleMutation.mutate(
        {
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
          data: {},
        },
        {
          onSuccess: () => {
            invalidateScheduleQuery();
            onClose();
            toast.success(t('automation.schedule.toast.resumeSuccess'));
          },
          onError: error => {
            setActionDialogType(null);

            errorHandler(error, () => {
              if (!checkIsAccessDeniedError(error)) {
                toast.error(t('automation.schedule.toast.resumeError'));
              }
              captureException(error as Error, {
                tags: {
                  component: 'ScheduleActionButtons',
                  action: 'handleResume',
                },
              });
            });
          },
        },
      );
    }
  };

  return (
    <>
      <Flex justify={isViewMode || isEditing ? 'between' : 'end'} width="full">
        {(isViewMode || isEditing) && (
          <Flex gap={2.5}>
            {isScheduleEnabled ? (
              canUpdateSchedule ? (
                <Button
                  variant="outline"
                  onClick={handlePause}
                  data-testid="schedule-triggers-pause-button"
                >
                  <Icon type="Pause" size="sm" />
                  {t('automation.schedule.action.pause')}
                </Button>
              ) : (
                <TooltipDisabledButton
                  tooltip={t('automation.schedule.action.disabled')}
                  icon="Pause"
                  variant="outline"
                  size="default"
                  data-testid="schedule-triggers-pause-button"
                >
                  {t('automation.schedule.action.pause')}
                </TooltipDisabledButton>
              )
            ) : canUpdateSchedule ? (
              <Button
                variant="outline"
                onClick={handleResume}
                data-testid="schedule-triggers-resume-button"
              >
                <Icon type="Play" size="sm" />
                {t('automation.schedule.action.resume')}
              </Button>
            ) : (
              <TooltipDisabledButton
                tooltip={t('automation.schedule.action.disabled')}
                icon="Play"
                variant="outline"
                size="default"
                data-testid="schedule-triggers-resume-button"
              >
                {t('automation.schedule.action.resume')}
              </TooltipDisabledButton>
            )}
            {canUpdateSchedule ? (
              <Button
                variant="destructive"
                onClick={handleDelete}
                data-testid="schedule-triggers-delete-button"
              >
                <Icon type="Trash" size="sm" />
                {t('automation.schedule.action.delete')}
              </Button>
            ) : (
              <TooltipDisabledButton
                tooltip={t('automation.schedule.action.disabled')}
                icon="Trash"
                variant="destructive"
                size="default"
                data-testid="schedule-triggers-delete-button"
              >
                {t('automation.schedule.action.delete')}
              </TooltipDisabledButton>
            )}
          </Flex>
        )}
        {isViewMode ? (
          canUpdateSchedule ? (
            <Button
              variant="outline"
              onClick={handleEdit}
              data-testid="schedule-triggers-edit-button"
            >
              {t('automation.schedule.action.edit')}
            </Button>
          ) : (
            <TooltipDisabledButton
              tooltip={t('automation.schedule.action.disabled')}
              variant="outline"
              size="default"
              data-testid="schedule-triggers-edit-button"
            >
              {t('automation.schedule.action.edit')}
            </TooltipDisabledButton>
          )
        ) : (
          <>
            <Flex gap={2.5}>
              <Button
                variant="outline"
                onClick={handleCancel}
                data-testid="schedule-triggers-cancel-button"
              >
                {t('common.action.cancel', { ns: 'common' })}
              </Button>
              {isEditing ? (
                canUpdateSchedule ? (
                  <Button
                    variant="default"
                    onClick={handleUpdate}
                    disabled={!isConfigValid}
                    data-testid="schedule-triggers-update-button"
                  >
                    {t('automation.schedule.action.update')}
                  </Button>
                ) : (
                  <TooltipDisabledButton
                    tooltip={t('automation.schedule.action.disabled')}
                    variant="default"
                    size="default"
                    data-testid="schedule-triggers-update-button"
                  >
                    {t('automation.schedule.action.update')}
                  </TooltipDisabledButton>
                )
              ) : canUpdateSchedule ? (
                <Button
                  onClick={handleSave}
                  data-testid="schedule-triggers-save-button"
                  disabled={isSaving || !isConfigValid}
                >
                  {t('automation.schedule.action.save')}
                </Button>
              ) : (
                <TooltipDisabledButton
                  tooltip={t('automation.schedule.action.disabled')}
                  variant="default"
                  size="default"
                  data-testid="schedule-triggers-save-button"
                >
                  {t('automation.schedule.action.save')}
                </TooltipDisabledButton>
              )}
            </Flex>
          </>
        )}

        {/* Action Confirmation Dialog (Delete or Update) */}
        {actionDialogType && (
          <ScheduleActionDialog
            type={actionDialogType}
            open={!!actionDialogType}
            onOpenChange={handleActionDialogOpenChange}
            onConfirm={handleActionConfirm}
          />
        )}
      </Flex>
    </>
  );
}
